import { normalizePrice } from "./utils";

// Extract and normalize item number from product spec
export const extractItemNumber = (productSpec) => {
  if (!productSpec) return "";

  const productSpecString = String(productSpec);

  const match = productSpecString.match(/^[\[](.*?)[\]]/);
  if (!match || !match[1]) return "";

  let itemNumber = match[1].trim().toLowerCase();

  const specialMappings = { "ls-3c": "ls-3c" };

  for (const prefix in specialMappings) {
    if (itemNumber.startsWith(prefix)) return specialMappings[prefix];
  }

  return itemNumber;
};

// Process PO name to standardized format
const formatPOName = (value, isABInvoice = false) => {
  if (!value) return "";

  // Clean and standardize the PO name
  let poName = value
    .toString()
    .replace(/[^\w\d\-\.]/g, "")
    .trim()
    .toUpperCase();

  if (!poName) return "";

  // Add "PO" prefix for AB invoices if missing
  if (isABInvoice && !poName.startsWith("PO")) {
    poName = "PO" + poName;
  }

  return poName;
};

const isNumericValue = (value) =>
  value !== "" && value !== null && value !== undefined && normalizePrice(value) !== "";

const normalizeExcelTotalPrice = (value) => {
  // Excel formula caches may contain floating tails such as 79.57499999999999.
  // Invoice totals have at most four meaningful decimal places, so round there
  // first before the final two-decimal display; this uses the Total Price cell,
  // never Quantity × Unit Price.
  return normalizePrice(normalizePrice(value, 4), 2);
};

const validateLTDItems = (items) => {
  if (!items.some((item) => item.poName && item.itemNumber)) {
    throw new Error(
      "Invoice format is invalid and cannot be parsed. Please contact IT.",
    );
  }

  return { items };
};

const formatLTDPOName = (value) => {
  const poNumber = String(value || "").match(/PO\D*(\d+)/i)?.[1];
  return poNumber ? `CN-PO${poNumber}` : "";
};

// Extract table data from Excel for LTD invoices
export const extractLTDTableData = (data) => {
  if (!data || data.length <= 6) {
    throw new Error("Invalid Excel file format, please check the file.");
  }

  const columns = [
    { index: 2, name: "poName" },
    { index: 4, name: "productSpec" },
    { index: 5, name: "quantity" },
    { index: 7, name: "unitPrice" },
    { index: 8, name: "totalPrice" },
  ];

  const result = [];
  for (let i = 7; i < data.length; i++) {
    const row = data[i];
    const dateValue = row[Object.keys(row)[0]];

    if (!dateValue) {
      break;
    }

    const item = {};
    columns.forEach(({ index, name }) => {
      const key = Object.keys(row)[index];
      let value = key ? row[key] : "";

      if (name === "poName") {
        value = formatPOName(value);
      } else if (name === "totalPrice") {
        value = normalizeExcelTotalPrice(value);
      } else if (name === "unitPrice") {
        value = normalizePrice(value);
      }

      item[name] = value;
    });

    item.itemNumber = extractItemNumber(item.productSpec);
    result.push(item);
  }

  return validateLTDItems(result);
};

// Extract table data from Excel for AB invoices
export const extractABTableData = (data) => {
  if (!data?.length) {
    throw new Error("Invalid AB Excel file format, please check the file.");
  }

  const startColumn = 1; // Start from column B (index 1)

  // Header is in row 12 (index 11), column definitions are as follows:
  // NO.    Item    PO    Unit Price(RMB)    Quantity    Amount(RMB)
  const columns = [
    { index: startColumn + 1, name: "productSpec" }, // Item column
    { index: startColumn + 2, name: "poName" }, // PO column
    { index: startColumn + 3, name: "unitPrice" }, // Unit Price(RMB) column
    { index: startColumn + 4, name: "quantity" }, // Quantity column
    { index: startColumn + 5, name: "totalPrice" }, // Amount(RMB) column
  ];

  const result = [];
  const startRow = data.findIndex(
    (row) =>
      row &&
      isNumericValue(row[startColumn]) &&
      Number(row[startColumn]) === 1,
  );

  if (startRow < 0) {
    throw new Error(
      "Invoice format is invalid and cannot be parsed. Please contact IT.",
    );
  }

  // Parse from the first serial-number row; stop at the first non-data row.
  for (let i = startRow; i < data.length; i++) {
    const row = data[i];

    if (!row || !isNumericValue(row[startColumn])) {
      break;
    }

    const item = {};

    // Process each column
    columns.forEach(({ index, name }) => {
      let value = row[index] !== undefined ? row[index] : "";

      // Apply specific formatting based on column type
      if (name === "poName") {
        value = formatPOName(value, true); // true indicates it's an AB invoice
      } else if (name === "totalPrice") {
        value = normalizeExcelTotalPrice(value);
      } else if (name === "unitPrice") {
        value = normalizePrice(value);
      }

      item[name] = value;
    });

    // Extract item number from product specification (using the same method as LTD)
    item.itemNumber = extractItemNumber(item.productSpec);
    result.push(item);
  }

  return {
    items: result,
  };
};

// LTD invoices currently use the AB invoice column layout.
export const extractLTDABTableData = (data) => {
  const items = extractABTableData(data).items.map((item) => ({
    ...item,
    poName: formatLTDPOName(item.poName),
  }));

  return validateLTDItems(items);
};
